import React from "react";

import { useQuery } from "@tanstack/react-query";
import { PageMain } from "components/molecules";
import { PaziaHeader } from "components/atoms/PaziaHeader/PaziaHeader";
import { PaziaLoader } from "components/atoms";
import { settingsApi } from "api";
import { ClientAppSettingsForm } from "./ClientAppSettingsForm";
import { SmtpSettingsForm } from "./SmtpSettingsForm";
import { SmsSettingsForm } from "./SmsSettingsForm";
import { AutopaySettingsForm } from "./AutopaySettingsForm";
import { InvoiceSellerSettingsForm } from "./InvoiceSellerSettingsForm";
import { InvoiceModeSettingsForm } from "./InvoiceModeSettingsForm";
import { DocumentsV2SettingsForm } from "./DocumentsV2SettingsForm";
import { AutopayRefundsSettingsForm } from "./AutopayRefundsSettingsForm";
import { TrainerZoneAdminNotificationEmailForm } from "./TrainerZoneAdminNotificationEmailForm";
import { SettingsTenantBanner } from "./SettingsTenantBanner";
import { SettingsReadinessPanel } from "./SettingsReadinessPanel";
import { findSettingValue, getSettingsReadiness, SETTINGS_SECTIONS } from "./settingsReadiness";

interface SettingsSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

// scroll-mt clears the sticky 4rem app header when a readiness link scrolls here.
const SettingsSection = ({ id, title, children }: SettingsSectionProps) => (
  <section
    id={id}
    className="scroll-mt-20 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
  >
    <h2 className="mb-3 text-lg font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
    {children}
  </section>
);

export const SettingsPage = (): JSX.Element | null => {
  // Same key and queryFn as useSmsDefaultSender, so both share one cache entry. Gate on isLoading (first
  // load only), never isFetching: a refetch after a save must not remount the forms and drop unsaved input.
  const { isLoading, data } = useQuery({
    queryKey: ["settings-list"],
    queryFn: () => settingsApi.getSettingsList(),
  });
  const settings = data?.data;

  return (
    <PageMain>
      <PaziaHeader>Ustawienia</PaziaHeader>
      <div className="mt-4 flex flex-col gap-6">
        <SettingsTenantBanner />
        {isLoading ? (
          <PaziaLoader position="center" />
        ) : (
          settings && (
            <>
              <SettingsReadinessPanel readiness={getSettingsReadiness(settings)} />

              <SettingsSection id={SETTINGS_SECTIONS.clientApp} title="Adresy aplikacji">
                <ClientAppSettingsForm
                  hostName={findSettingValue(settings, "client-app-hostname")}
                  trainerAppHostName={findSettingValue(settings, "trainer-app-hostname")}
                  resetPasswordPage={findSettingValue(settings, "client-app-reset-password-page")}
                  loginPage={findSettingValue(settings, "client-app-login-page")}
                  coursePaymentsPage={findSettingValue(settings, "client-app-course-payments-page")}
                  courseSchedulePage={findSettingValue(settings, "client-app-course-schedule-page")}
                  courseExamsPage={findSettingValue(settings, "client-app-course-exams-page")}
                  settingsPage={findSettingValue(settings, "client-app-settings-page")}
                />
              </SettingsSection>

              <SettingsSection id={SETTINGS_SECTIONS.smtp} title="Poczta (SMTP)">
                <SmtpSettingsForm
                  host={findSettingValue(settings, "smtp-host")}
                  port={findSettingValue(settings, "smtp-port")}
                  username={findSettingValue(settings, "smtp-username")}
                  fromEmail={findSettingValue(settings, "smtp-from-email")}
                  fromName={findSettingValue(settings, "smtp-from-name")}
                />
              </SettingsSection>

              <SettingsSection id={SETTINGS_SECTIONS.sms} title="SMS">
                <SmsSettingsForm defaultSender={findSettingValue(settings, "sms-default-sender")} />
              </SettingsSection>

              <SettingsSection id={SETTINGS_SECTIONS.autopay} title="Płatności (Autopay)">
                <AutopaySettingsForm
                  serviceId={findSettingValue(settings, "autopay-service-id")}
                  autopayApiUrl={findSettingValue(settings, "autopay-api-url")}
                />
              </SettingsSection>

              <SettingsSection id={SETTINGS_SECTIONS.invoices} title="Faktury">
                <div className="flex flex-col gap-6">
                  <InvoiceSellerSettingsForm
                    name={findSettingValue(settings, "invoice-seller-name")}
                    address={findSettingValue(settings, "invoice-seller-address")}
                    nip={findSettingValue(settings, "invoice-seller-nip")}
                    bankAccount={findSettingValue(settings, "invoice-seller-bank-account")}
                    footerOwner={findSettingValue(settings, "invoice-footer-owner")}
                    footerPhone={findSettingValue(settings, "invoice-footer-phone")}
                    footerEmail={findSettingValue(settings, "invoice-footer-email")}
                    invoiceNumberSeries={findSettingValue(settings, "invoice-number-series")}
                    logoUrl={findSettingValue(settings, "invoice-logo-url")}
                  />
                  <InvoiceModeSettingsForm
                    // Case-insensitive: a C# bool.ToString() would store "True".
                    useKsefInvoices={findSettingValue(settings, "use-ksef-invoices").trim().toLowerCase() === "true"}
                  />
                </div>
              </SettingsSection>

              <SettingsSection id="settings-autopay-refunds" title="Zwroty">
                <AutopayRefundsSettingsForm
                  // Case-insensitive: a C# bool.ToString() would store "True".
                  enabled={findSettingValue(settings, "autopay-refunds-enabled").trim().toLowerCase() === "true"}
                />
              </SettingsSection>

              <SettingsSection id="settings-documents" title="Dokumenty">
                <DocumentsV2SettingsForm
                  // Case-insensitive: a C# bool.ToString() would store "True".
                  enabled={findSettingValue(settings, "documents-v2-enabled").trim().toLowerCase() === "true"}
                />
              </SettingsSection>

              <SettingsSection id={SETTINGS_SECTIONS.notifications} title="Powiadomienia">
                <TrainerZoneAdminNotificationEmailForm
                  email={findSettingValue(settings, "trainer-zone-admin-notification-email")}
                />
              </SettingsSection>
            </>
          )
        )}
      </div>
    </PageMain>
  );
};
